import { tr } from 'date-fns/locale';
import { useState, useEffect, useCallback } from 'react';
import { format, subDays, parseISO, endOfDay, startOfDay } from 'date-fns';

import Tooltip from '@mui/material/Tooltip';
import {
  Box,
  Grid,
  Table,
  Paper,
  Alert,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  Typography,
  TableContainer,
  CircularProgress,
} from '@mui/material';

import { attendanceApi, type Attendance, ATTENDANCE_REASON_LABELS_TR } from 'src/api/attendance.api';

import { Label } from 'src/components/label';

import {
  isOngoing,
  formatDurationTr,
  attendanceStatusLabel,
  attendanceStatusColor,
  AUTO_CHECKOUT_LABEL_TR,
  AUTO_CHECKOUT_TOOLTIP_TR,
  durationMinutesFromTimestamps,
} from 'src/sections/attendance/utils/attendance-status';

interface EmployeeAttendanceTabProps {
  employeeId: string;
}

export default function EmployeeAttendanceTab({ employeeId }: EmployeeAttendanceTabProps) {
  const [attendances, setAttendances] = useState<Attendance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));

  const fetchAttendances = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const start = startOfDay(new Date(startDate));
      const end = endOfDay(new Date(endDate));
      const data = await attendanceApi.getEmployeeAttendance(employeeId, start, end);
      setAttendances(data);
    } catch (err: any) {
      setError(err.message || 'Devam kayıtları yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [employeeId, startDate, endDate]);

  useEffect(() => {
    fetchAttendances();
  }, [fetchAttendances]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Devam Kayıtları ({attendances.length})</Typography>
      </Stack>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Başlangıç Tarihi"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Tarih değiştirince otomatik yenilenir"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Bitiş Tarihi"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Tarih değiştirince otomatik yenilenir"
          />
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : attendances.length === 0 ? (
        <Alert severity="info">Seçilen tarih aralığında devam kaydı bulunmamaktadır.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Giriş</TableCell>
                <TableCell>Çıkış</TableCell>
                <TableCell>Çalışma Saati</TableCell>
                <TableCell>Şube</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Sebep</TableCell>
                <TableCell>Sapma</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attendances.map((attendance) => {
                const ongoing = isOngoing(attendance);
                const mins = ongoing
                  ? durationMinutesFromTimestamps(attendance.checkInTime, new Date().toISOString())
                  : durationMinutesFromTimestamps(attendance.checkInTime, attendance.checkOutTime);
                return (
                  <TableRow key={attendance.id} hover>
                    <TableCell>
                      {format(parseISO(attendance.checkInTime), 'dd MMM HH:mm', { locale: tr })}
                    </TableCell>
                    <TableCell>
                      {attendance.checkOutTime ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>{format(parseISO(attendance.checkOutTime), 'dd MMM HH:mm', { locale: tr })}</span>
                          {attendance.autoCheckout && (
                            <Tooltip title={AUTO_CHECKOUT_TOOLTIP_TR}>
                              <Label variant="soft" color="warning">{AUTO_CHECKOUT_LABEL_TR}</Label>
                            </Tooltip>
                          )}
                        </Stack>
                      ) : (
                        <Label variant="soft" color="info">Devam ediyor</Label>
                      )}
                    </TableCell>
                    <TableCell>
                      {mins != null ? (
                        <Typography variant="body2">
                          {formatDurationTr(mins)}
                          {ongoing ? ' · devam ediyor' : ''}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>{attendance.branch?.name || '-'}</TableCell>
                    <TableCell>
                      <Label variant="soft" color={attendanceStatusColor(attendance.status)}>
                        {attendanceStatusLabel(attendance.status)}
                      </Label>
                    </TableCell>
                    <TableCell>
                      {attendance.reason ? (
                        <Tooltip title={attendance.reasonDescription || ''}>
                          <Typography variant="body2">
                            {ATTENDANCE_REASON_LABELS_TR[attendance.reason]}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {attendance.deviationMinutes != null ? (
                        <Typography
                          variant="body2"
                          color={attendance.deviationMinutes > 0 ? 'warning.main' : 'info.main'}
                        >
                          {attendance.deviationMinutes > 0 ? '+' : ''}
                          {attendance.deviationMinutes}dk
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
